// Open GitHub's OAuth authorize URL in a popup, wait for the
// callback redirect to land on /workspaces/:id?github=connected,
// detect that landing in the popup, post a message back to opener,
// and close. Original tab keeps its state and refreshes the
// connection list as soon as the popup signals success.
//
// The actual postMessage emit + window.close happens in the
// WorkspaceRoomPage's mount effect (it sees ?github=connected and
// window.opener and acts). Here we just open the popup, wire the
// listener, and resolve the promise on success or rejection on
// timeout / popup closed early.

const POPUP_NAME = "wt-gh-oauth";
// The callback page must be able to postMessage back to the opener.
// Do not include a noopener feature here; some browsers treat the
// feature name as enabling noopener regardless of its string value.
const POPUP_FEATURES = "width=720,height=820,popup=1";

export interface GithubOauthPopupResult {
  ok: boolean;
  workspaceId: number;
  reason?: "closed" | "timeout";
}

export function openGithubOauthPopup(
  url: string,
  workspaceId: number,
  timeoutMs = 5 * 60_000,
): Promise<GithubOauthPopupResult> {
  return new Promise((resolve) => {
    const popup = window.open(url, POPUP_NAME, POPUP_FEATURES);
    if (!popup) {
      // Popup blocked — fall back to same-tab navigation so the
      // user still gets through the OAuth flow even with a strict
      // browser.
      window.location.href = url;
      // We don't actually resolve here — the navigation away
      // unmounts the listener, and the post-callback /workspaces/:id
      // page just renders fresh. Promise stays pending until GC.
      return;
    }

    let settled = false;
    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      window.clearInterval(closeTimer);
      window.clearTimeout(deadline);
    };

    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== window.origin) return;
      const data = ev.data as { type?: string; workspaceId?: number } | null;
      if (data?.type !== "wt-gh-connected") return;
      if (data.workspaceId !== workspaceId) return;
      if (settled) return;
      settled = true;
      cleanup();
      try {
        popup.close();
      } catch {
        /* popup may already be closed */
      }
      resolve({ ok: true, workspaceId });
    };
    window.addEventListener("message", onMessage);

    // Detect popup closed without auth — mid-flow user dismissal.
    const closeTimer = window.setInterval(() => {
      if (settled) return;
      if (popup.closed) {
        settled = true;
        cleanup();
        resolve({ ok: false, workspaceId, reason: "closed" });
      }
    }, 500);

    const deadline = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      try {
        popup.close();
      } catch {
        /* ignore */
      }
      resolve({ ok: false, workspaceId, reason: "timeout" });
    }, timeoutMs);
  });
}
